/**
 * Generate reward keys from mint time to current month
 * Format: "year-month" (e.g., "2024-6" for June 2024, without leading zeros)
 */
export function generateRewardKeys(mintTime: bigint): string[] {
	const mintDate = new Date(Number(mintTime) * 1000);
	const currentDate = new Date();
	
	const keys: string[] = [];
	
	// Start from the mint month
	let year = mintDate.getFullYear();
	let month = mintDate.getMonth() + 1; // getMonth() returns 0-11, so add 1
	
	// Generate keys until current month
	while (year < currentDate.getFullYear() || 
		   (year === currentDate.getFullYear() && month <= currentDate.getMonth() + 1)) {
		keys.push(`${year}-${month}`);
		
		// Move to next month
		month++;
		if (month > 12) {
			month = 1;
			year++;
		}
	}
	
	return keys;
}

/**
 * Format reward key for display (e.g., "2024-6" -> "June 2024")
 */
export function formatRewardKey(key: string): string {
	const [year, month] = key.split('-');
	const monthNames = [
		'January', 'February', 'March', 'April', 'May', 'June',
		'July', 'August', 'September', 'October', 'November', 'December'
	];
	const monthIndex = parseInt(month, 10) - 1;
	return `${monthNames[monthIndex]} ${year}`;
}

/**
 * Get month name from reward key (e.g., "2024-6" -> "June")
 */
export function getMonthName(key: string): string {
	const [, month] = key.split('-');
	const monthNames = [
		'January', 'February', 'March', 'April', 'May', 'June',
		'July', 'August', 'September', 'October', 'November', 'December'
	];
	const monthIndex = parseInt(month, 10) - 1;
	return monthNames[monthIndex];
}

/**
 * Get year from reward key (e.g., "2024-6" -> "2024")
 */
export function getYear(key: string): string {
	const [year] = key.split('-');
	return year;
}

/**
 * Group reward keys by year
 */
export function groupRewardKeysByYear(keys: string[]): Record<string, string[]> {
	const grouped: Record<string, string[]> = {};
	
	keys.forEach(key => {
		const year = getYear(key);
		if (!grouped[year]) {
			grouped[year] = [];
		}
		grouped[year].push(key);
	});
	
	// Sort months within each year (they should already be sorted, but just in case)
	Object.keys(grouped).forEach(year => {
		grouped[year].sort((a, b) => {
			const [, monthA] = a.split('-');
			const [, monthB] = b.split('-');
			return parseInt(monthA, 10) - parseInt(monthB, 10);
		});
	});
	
	return grouped;
}
